import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

/** Sort direction as the list endpoints expect it. */
export type ListSortMode = 'asc' | 'desc';

/**
 * The complete, serializable description of a list view: everything that must
 * survive navigating into a record and back out again.
 *
 * `filter` is the already-encoded advanced-filter string (the output of
 * ``encodeFilterObject``), not the filter object — this service deliberately
 * knows nothing about per-module filter shapes.
 *
 * `pill` is the host's status-pill key. Its vocabulary is owned by the host
 * component (see ``IListViewDefaults.pills``); to this service it is an opaque
 * string, so the same plumbing serves the order pills, the import pills, etc.
 */
export interface IListViewState {
  page: number;
  size: number;
  keyword: string;
  filter: string | null;
  pill: string;
  sortBy: string;
  sortMode: ListSortMode;
}

/**
 * Per-surface defaults and the whitelists used to sanitise inbound query
 * params. A URL is user-editable and can be stale or hand-mangled, so every
 * value read back out of one falls through to these unless it is recognised.
 */
export interface IListViewDefaults {
  size: number;
  pill: string;
  sortBy: string;
  sortMode: ListSortMode;
  /** Accepted page sizes; anything else falls back to `size`. */
  pageSizes: number[];
  /** Accepted pill keys. Omit to accept any non-empty string. */
  pills?: string[];
  /** Accepted sort keys. Omit to accept any non-empty string. */
  sortKeys?: string[];
}

/** Query-param names this service owns. Hosts must not reuse them. */
const PARAM = {
  page: 'page',
  size: 'count',
  keyword: 'keyword',
  filter: 'filter',
  pill: 'pill',
  sortBy: 'sort_by',
  sortMode: 'sort_mode',
} as const;

/**
 * Single source of truth for "where the user was" in a paginated list: page,
 * page size, search keyword, advanced filter, status pill and sort — plus the
 * one-shot row anchor used to put them back at the row they opened.
 *
 * The view itself round-trips through the **URL**, which makes a filtered list
 * shareable and bookmarkable and — the reason this exists (WB-8072) — means it
 * survives every return path, including breadcrumbs and the sidebar, not just
 * the in-app back button.
 *
 * The row anchor (WB-7867) is held in memory instead, keyed by table. It is
 * navigation-scoped state, not part of the view's identity: pasting someone a
 * list URL should not scroll them to a row they never clicked. Being in-memory
 * also gives it exactly the lifetime we want — it survives the component being
 * destroyed and recreated, and dies on a full page reload.
 *
 * Anchoring is by **row id, not scroll offset**, on purpose. Acting on a record
 * can change it (validating an order changes its status), so under an active
 * pill the row may leave the list entirely and everything above it reflows; a
 * remembered pixel offset would land the user somewhere arbitrary. A missing
 * anchor row degrades to "stay at the top", which is the old behaviour.
 */
@Injectable({ providedIn: 'root' })
export class ListViewStateService {
  /** Pending row anchors, keyed by table key. Consumed once, then dropped. */
  private readonly anchors = new Map<string, string>();

  /**
   * Build a view state from a URL's query params, sanitising every field
   * against `defaults`. Missing and unrecognised values fall back rather than
   * throw, so a stale or hand-edited URL still renders a usable list.
   */
  fromQueryParams(params: Params, defaults: IListViewDefaults): IListViewState {
    return {
      page: this.readPage(params[PARAM.page]),
      size: this.readSize(params[PARAM.size], defaults),
      keyword: typeof params[PARAM.keyword] === 'string' ? params[PARAM.keyword] : '',
      filter: params[PARAM.filter] ? String(params[PARAM.filter]) : null,
      pill: this.readFromWhitelist(params[PARAM.pill], defaults.pills, defaults.pill),
      sortBy: this.readFromWhitelist(params[PARAM.sortBy], defaults.sortKeys, defaults.sortBy),
      sortMode: params[PARAM.sortMode] === 'asc' || params[PARAM.sortMode] === 'desc' ? params[PARAM.sortMode] : defaults.sortMode,
    };
  }

  /**
   * Serialise a view state for `router.navigate`.
   *
   * Every owned param is always present, with `null` for "not set" rather than
   * being omitted — so the result is safe to pass with
   * `queryParamsHandling: 'merge'`: it clears the params it owns while leaving
   * any param belonging to another feature untouched. Omitting them instead
   * would let a dropped filter or pill silently persist in the URL.
   *
   * Values equal to their default are emitted as `null` too, which keeps the
   * common case (an untouched list) at a clean `?page=1&count=25`.
   */
  toQueryParams(state: IListViewState, defaults: IListViewDefaults): Params {
    const sortIsDefault = state.sortBy === defaults.sortBy && state.sortMode === defaults.sortMode;
    return {
      [PARAM.page]: state.page,
      [PARAM.size]: state.size,
      [PARAM.keyword]: state.keyword ? state.keyword : null,
      [PARAM.filter]: state.filter ?? null,
      [PARAM.pill]: state.pill && state.pill !== defaults.pill ? state.pill : null,
      [PARAM.sortBy]: sortIsDefault ? null : state.sortBy,
      [PARAM.sortMode]: sortIsDefault ? null : state.sortMode,
    };
  }

  /**
   * The view a surface shows before the user has touched anything. No caller yet —
   * it is the default the query-param round-trip specs build on.
   */
  defaultState(defaults: IListViewDefaults): IListViewState {
    return {
      page: 1,
      size: defaults.size,
      keyword: '',
      filter: null,
      pill: defaults.pill,
      sortBy: defaults.sortBy,
      sortMode: defaults.sortMode,
    };
  }

  /**
   * Record the row the user is navigating into, so the list can scroll back to
   * it on return. Overwrites any anchor already pending for `tableKey` — the
   * most recent navigation is the one worth restoring.
   */
  rememberAnchor(tableKey: string, rowId: string | number): void {
    this.anchors.set(tableKey, String(rowId));
  }

  /**
   * Take the pending anchor for `tableKey`, clearing it. Returns `null` when
   * there is none. Consuming is deliberate: the anchor must fire once, on the
   * first render after returning, and not again when the user pages or re-sorts.
   */
  consumeAnchor(tableKey: string): string | null {
    const rowId = this.anchors.get(tableKey);
    if (rowId === undefined) return null;
    this.anchors.delete(tableKey);
    return rowId;
  }

  /**
   * Scroll the row carrying `[attr.data-row-id]="rowId"` into the middle of
   * `container`. Returns whether the row was found — callers that care can
   * treat `false` as "the record is no longer in this view".
   *
   * Scheduled on a macrotask so it runs after Angular has rendered the rows
   * the caller just received; `scrollIntoView` on a row that does not exist
   * yet would silently do nothing.
   */
  restoreAnchor(container: HTMLElement | null | undefined, rowId: string | number, attribute = 'data-row-id'): boolean {
    if (!container) return false;
    const row = container.querySelector<HTMLElement>(`[${attribute}="${CSS.escape(String(rowId))}"]`);
    if (!row) return false;
    row.scrollIntoView({ block: 'center' });
    return true;
  }

  private readPage(raw: unknown): number {
    const page = parseInt(String(raw ?? ''), 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  }

  private readSize(raw: unknown, defaults: IListViewDefaults): number {
    const size = parseInt(String(raw ?? ''), 10);
    return defaults.pageSizes.includes(size) ? size : defaults.size;
  }

  private readFromWhitelist(raw: unknown, allowed: string[] | undefined, fallback: string): string {
    if (typeof raw !== 'string' || raw === '') return fallback;
    if (allowed && !allowed.includes(raw)) return fallback;
    return raw;
  }
}
